/*
 * File: CommentFeed.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 4, 2022 at 4:30 PM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 5, 2025 at 8:26 AM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	memo, useCallback, useEffect, useState
} from 'react';
import PropTypes from 'prop-types';
import Lottie from 'react-lottie-player';
import { replaceColor } from 'lottie-colorify';
import { useSelector } from 'react-redux';

// Animation
import animationData from '../../assets/animations/spinner.json';

// Services
import { fetchComments } from '../../services/comment';

// Components
import { Comment } from '../Comment';
import { InfiniteScroll } from '../InfiniteScroll';

// Constants
import { SOURCES, UI_MODE_OPTIONS } from '../../../constants/base.constants';

// Styles
import * as S from './CommentFeed.styles';
import { DarkTheme, LightTheme } from '../../styles/colors';


/**
 * Component
 */

export const CommentFeed = memo(({
	className, source, sourceId, pendingComments
}) => {

	// Create state handlers
	const [comments, setComments] = useState([]);
	const [currentPage, setCurrentPage] = useState(0);
	const [hasMoreToLoad, setMoreToLoad] = useState(true);
	const [isFeedLoading, setIsFeedLoading] = useState(false);

	// Get partner if necessary
	const partner = useSelector((state) => state.partner.value);

	// Get current UI mode from hook
	const uiMode = useSelector((state) => state.ui.value);

	// Define current theme
	const currentTheme = uiMode.mode === UI_MODE_OPTIONS.DARK ? DarkTheme(partner) : LightTheme(partner);

	// Handle fetch comment feed
	const fetchCommentFeed = async ({ page }) => {

		// Update state
		setIsFeedLoading(true);

		// Fetch question previews
		const { data: { result: { comments: commentFeed } } } = await fetchComments({
			source,
			sourceId,
			page
		});

		// Update comments
		setComments(page === 0 ? commentFeed : [...comments, ...commentFeed]);

		// Update page state
		setCurrentPage(page);

		// Update more to load state
		setMoreToLoad(commentFeed.length > 5);

		// Update state
		setIsFeedLoading(false);
	};

	// Handle actions on source id update
	useEffect(() => {

		// Fetch comments
		fetchCommentFeed({ page: 0 });

	}, [sourceId]);

	// Handle actions on component load
	useEffect(() => {}, []);

	// Handle render threads
	const renderThreads = useCallback((commentArray) => commentArray.map((comment) => (
		<S.ThreadContainer $depth={comment.depth} key={comment.id}>
			<Comment comment={comment} source={source} sourceId={sourceId} postCreateActions={() => { /* TODO */ }} />
			{renderThreads(comment.comments)}

			{/* TODO - HAS MORE BUTTON */}

		</S.ThreadContainer>
	)), []);

	// Render component
	return (
		<S.Wrapper className={className}>
			<InfiniteScroll
				dataLength={comments.length}
				pullToRefresh={false}
				next={() => { if (!isFeedLoading) fetchCommentFeed({ page: currentPage + 1 }); }}
				refresh={async () => { await fetchCommentFeed({ page: 0 }); return true; }}
				loader={(
					<S.LoaderContainer>
						<Lottie loop play animationData={replaceColor('#ffffff', currentTheme.brandPrimaryBase, animationData)} style={{ height: '100%' }} />
					</S.LoaderContainer>
				)}
				hasMore={hasMoreToLoad}
			>
				<S.Threads>
					{renderThreads(comments)}
				</S.Threads>
			</InfiniteScroll>
		</S.Wrapper>
	);
});


/**
 * Configuration
 */

CommentFeed.displayName = 'CommentFeed';
CommentFeed.propTypes = {
	className: PropTypes.string,
	source: PropTypes.oneOf([SOURCES.STORY, SOURCES.QUESTION, SOURCES.MESSAGE]),
	sourceId: PropTypes.string,
	pendingComments: PropTypes.arrayOf(PropTypes.shape())
};
CommentFeed.defaultProps = {
	className: null,
	source: null,
	sourceId: null,
	pendingComments: []
};
